import React, { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import { Waves, ArrowLeft, CreditCard, Sparkles, Loader2, ShieldCheck } from "lucide-react";
import { useTranslation } from "../lib/i18n";
import { useAppSettings } from "../context/AppSettingsContext";

const PacksStore = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [paymentSettings, setPaymentSettings] = useState({ stripe_enabled: true, mbway_enabled: true, whatsapp_number: "", payments_active: true });
  const { t } = useTranslation();
  const { settings } = useAppSettings();

  useEffect(() => {
    fetchPacks();
    fetchPaymentSettings();
  }, []);

  const fetchPacks = async () => {
    try {
      const res = await axios.get(`${API}/packs`);
      setPacks(res.data.sort((a, b) => a.sort_order - b.sort_order));
    } catch (e) {
      toast.error("Erro ao carregar packs");
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings/public`);
      setPaymentSettings(res.data);
    } catch {
      // defaults are fine
    }
  };

  const handlePurchase = async (pack) => {
    if (!paymentSettings.payments_active) {
      const phone = paymentSettings.whatsapp_number?.replace(/[^0-9]/g, '') || "351961410360";
      const msg = encodeURIComponent(`Olá! Gostava de comprar o pack "${pack.name}" (${pack.credits} aulas). Podem ajudar-me?`);
      window.open(`https://api.whatsapp.com/send?phone=${phone}&text=${msg}`, "_blank");
      return;
    }
    setPurchasing(pack.pack_id);
    try {
      // Use configured frontend_url for payment redirects
      const origin_url = settings.frontend_url || window.location.origin;
      const res = await axios.post(`${API}/checkout`, {
        pack_id: pack.pack_id,
        origin_url: origin_url,
      }, { withCredentials: true });

      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (e) {
      toast.error("Erro ao iniciar pagamento. Tenta novamente.");
      setPurchasing(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f5f5f5] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#6697a4]" />
      </div>
    );
  }

  const groupPacks = packs.filter(p => p.pack_type === "group");
  const privatePacks = packs.filter(p => p.pack_type === "private");

  return (
    <div className="min-h-screen bg-[#f5f5f5]">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} data-testid="back-to-dashboard">
            <ArrowLeft className="h-4 w-4 mr-1" /> {t("back")}
          </Button>
        </div>

        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold text-[#3d5a63] mb-2">{t("buyLessonsTitle")}</h1>
          <p className="text-[#94A3B8]">{t("choosePack")}</p>
          {user && (
            <div className="flex items-center justify-center gap-4 mt-4">
              <Badge variant="outline" className="text-sm px-3 py-1">
                <Waves className="h-3.5 w-3.5 mr-1.5 text-[#6697a4]" />
                {user.credits || 0} {t("groupCreditsLabel")}
              </Badge>
              <Badge variant="outline" className="text-sm px-3 py-1">
                <Sparkles className="h-3.5 w-3.5 mr-1.5 text-[#e8927c]" />
                {user.private_credits || 0} {t("privateCreditsLabel")}
              </Badge>
            </div>
          )}
        </div>

        {/* Group Packs */}
        {groupPacks.length > 0 && (
          <div className="mb-10">
            <h2 className="text-lg font-semibold text-[#3d5a63] mb-4 flex items-center gap-2">
              <Waves className="h-5 w-5 text-[#6697a4]" /> {t("groupLessons")}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {groupPacks.map((pack) => (
                <Card 
                  key={pack.pack_id} 
                  className={`relative overflow-hidden transition-all hover:shadow-lg ${pack.popular ? 'ring-2 ring-[#e8927c]' : ''}`}
                  data-testid={`pack-card-${pack.pack_id}`}
                >
                  {pack.popular && (
                    <div className="absolute top-0 right-0">
                      <Badge className="rounded-none rounded-bl-lg bg-[#e8927c] text-white border-0">{t("popular")}</Badge>
                    </div>
                  )}
                  <CardContent className="pt-6 pb-4">
                    <p className="text-xs text-[#94A3B8] uppercase tracking-wider mb-1">Surf / Bodyboard</p>
                    <h3 className="text-lg font-bold text-[#3d5a63] mb-1">{pack.name}</h3>
                    <p className="text-sm text-[#94A3B8] mb-4">{pack.description}</p>
                    <div className="flex items-baseline gap-1 mb-4">
                      <span className="text-3xl font-bold text-[#3d5a63]">{pack.price}€</span>
                      <span className="text-sm text-[#94A3B8]">/ {pack.credits} aula{pack.credits > 1 ? 's' : ''}</span>
                    </div>
                    <Button 
                      onClick={() => handlePurchase(pack)}
                      disabled={purchasing === pack.pack_id}
                      className="w-full bg-[#6697a4] hover:bg-[#3d5a63]"
                      data-testid={`buy-pack-${pack.pack_id}`}
                    >
                      {purchasing === pack.pack_id ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <CreditCard className="h-4 w-4 mr-2" />
                      )}
                      {t("buy")}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Private Packs */}
        {privatePacks.length > 0 && (
          <div className="mb-10">
            <h2 className="text-lg font-semibold text-[#3d5a63] mb-4 flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-[#e8927c]" /> {t("privateLessons")}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {privatePacks.map((pack) => (
                <Card 
                  key={pack.pack_id} 
                  className="relative overflow-hidden transition-all hover:shadow-lg border-[#e8927c]/20"
                  data-testid={`pack-card-${pack.pack_id}`}
                >
                  <CardContent className="pt-6 pb-4">
                    <p className="text-xs text-[#e8927c] uppercase tracking-wider mb-1">{t("private")}</p>
                    <h3 className="text-lg font-bold text-[#3d5a63] mb-1">{pack.name}</h3>
                    <p className="text-sm text-[#94A3B8] mb-4">{pack.description}</p>
                    <div className="flex items-baseline gap-1 mb-4">
                      <span className="text-3xl font-bold text-[#3d5a63]">{pack.price}€</span>
                      <span className="text-sm text-[#94A3B8]">{t("perPerson")}</span>
                    </div>
                    <Button 
                      onClick={() => handlePurchase(pack)}
                      disabled={purchasing === pack.pack_id}
                      className="w-full bg-[#e8927c] hover:bg-[#d4735d]"
                      data-testid={`buy-pack-${pack.pack_id}`}
                    >
                      {purchasing === pack.pack_id ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <CreditCard className="h-4 w-4 mr-2" />
                      )}
                      {t("buy")}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Security badge */}
        <div className="text-center text-sm text-[#94A3B8] flex items-center justify-center gap-2 mt-4">
          <ShieldCheck className="h-4 w-4" />
          {t("securePayment")}
        </div>
      </div>
    </div>
  );
};

export default PacksStore;
